import { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, X, Eye, ChevronDown, Ban, Copy } from 'lucide-react';
import {
  getOrders, createOrder, updateOrder, deleteOrder,
  updateOrderStatus, updateOrderPayment, banOrderFingerprint,
} from '../api/orders';
import { getProducts, getProductVariants } from '../api/products';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWriteOrders } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

// ── Constants ────────────────────────────────────────────────────────────────

const NEXT_STATUSES = {
  PENDING:    ['CONFIRMED', 'ON_THE_WAY', 'CANCELLED'],
  CONFIRMED:  ['PENDING', 'ON_THE_WAY', 'CANCELLED'],
  ON_THE_WAY: ['DELIVERED', 'CANCELLED', 'RETURNED'],
  DELIVERED:  ['RETURNED'],
  CANCELLED:  ['PENDING'],
  RETURNED:   ['PENDING'],
};

const STATUS_STYLE = {
  PENDING:    'bg-gray-100 text-gray-600',
  CONFIRMED:  'bg-blue-50 text-blue-700',
  ON_THE_WAY: 'bg-purple-50 text-purple-700',
  DELIVERED:  'bg-green-50 text-green-700',
  CANCELLED:  'bg-red-50 text-red-600',
  RETURNED:   'bg-orange-50 text-orange-600',
};

const PAYMENT_METHOD_KEYS = ['CASH', 'BKASH', 'NAGAD', 'ROCKET', 'CARD', 'BANK_TRANSFER', 'OTHER'];

const EMPTY_FORM = {
  customerName: '', customerPhone: '', customerEmail: '', customerAddress: '',
  deliveryAddress: '', notes: '', paymentStatus: 'UNPAID', paymentMethod: 'CASH',
  manualDiscount: 0,
  deliveryCharge: 0,
  items: [{ productId: '', quantity: 1, variantCombo: null, variantId: null }],
};

// ── Small components ──────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const { t } = useTranslation();
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLE[status]}`}>
      {t(`status.${status}`)}
    </span>
  );
}

function InlineStatusChanger({ order, onStatusChange, isPending }) {
  const { t } = useTranslation();
  const available = NEXT_STATUSES[order.status] ?? [];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (available.length === 0) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left });
    setOpen((o) => !o);
  };

  const visible = [order.status, ...available];

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1 rounded-lg ring-1 ring-inset ring-black/10 transition-opacity ${STATUS_STYLE[order.status]} ${
          available.length > 0 ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
        }`}
      >
        {t(`status.${order.status}`)}
        {available.length > 0 && <ChevronDown size={12} strokeWidth={2.5} />}
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-2 space-y-1 min-w-[170px]"
        >
          {visible.map((s) => {
            const isCurrent = s === order.status;
            return (
              <button
                key={s}
                onClick={() => { if (!isCurrent) { onStatusChange(order.id, s); setOpen(false); } }}
                disabled={isCurrent || isPending}
                className={`w-full text-left text-sm font-medium px-3 py-2 rounded-lg flex items-center justify-between gap-3 ${STATUS_STYLE[s]} ${
                  isCurrent
                    ? 'cursor-default ring-1 ring-inset ring-black/10'
                    : 'hover:opacity-80 transition-opacity cursor-pointer'
                } disabled:cursor-default`}
              >
                <span>{t(`status.${s}`)}</span>
                {isCurrent && <span className="text-xs font-bold">✓</span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}

function PaymentBadge({ status, onClick }) {
  const { t } = useTranslation();
  const paid = status === 'PAID';
  return (
    <button
      onClick={onClick}
      title="Click to toggle payment status"
      className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full transition-colors ${
        paid ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
      }`}
    >
      {t(`status.${status}`)}
    </button>
  );
}

function MethodBadge({ method }) {
  const { t } = useTranslation();
  if (!method) return null;
  return (
    <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
      {t(`paymentMethods.${method}`, method)}
    </span>
  );
}

function fmt(value) {
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCombo(combo) {
  if (!combo || Object.keys(combo).length === 0) return null;
  return Object.entries(combo).map(([k, v]) => `${k}: ${v}`).join(', ');
}

// ── Order Detail Modal ────────────────────────────────────────────────────────

function OrderDetail({ order, onClose, onPaymentChange, onBanRequest }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyFingerprint = (value) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Modal title={`Order #${order.token}`} onClose={onClose} wide>
      <div className="space-y-5">
        {/* Customer */}
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('common.customer')}</p>
          <p className="font-medium text-gray-800">{order.customer.name}</p>
          <p className="text-sm text-gray-500">{order.customer.phone}</p>
          {order.customer.email && <p className="text-sm text-gray-400">{order.customer.email}</p>}
          {order.deliveryAddress && (
            <p className="text-sm text-gray-500 mt-1">📍 {order.deliveryAddress}</p>
          )}
          {order.takenBy && (
            <p className="text-xs text-gray-400 mt-1">Taken by: <span className="font-medium">{order.takenBy}</span></p>
          )}
        </div>

        {/* Device fingerprint */}
        {order.fingerprintKey && (
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('orders.deviceFingerprint')}</p>
            <div className="flex items-center gap-2">
              <code className="text-xs font-mono text-gray-600">
                {order.fingerprintKey.slice(0, 12)}…{order.fingerprintKey.slice(-8)}
              </code>
              <button type="button" onClick={() => copyFingerprint(order.fingerprintKey)} className="text-gray-400 hover:text-gray-600" title={t('orders.copyFingerprint')}>
                <Copy size={13} />
              </button>
              {copied && <span className="text-xs text-green-600">{t('orders.copied')}</span>}
              {order.fingerprintBanned && (
                <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                  {t('orders.deviceBanned')}
                </span>
              )}
            </div>
            {onBanRequest && (
              <button
                type="button"
                onClick={onBanRequest}
                className={`mt-2 flex items-center gap-1.5 text-sm font-medium ${
                  order.fingerprintBanned ? 'text-red-600 hover:text-red-700' : 'text-orange-600 hover:text-orange-700'
                }`}
              >
                <Ban size={14} /> {order.fingerprintBanned ? t('orders.manageBan') : t('orders.banDevice')}
              </button>
            )}
          </div>
        )}

        {/* Items */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t('common.items')}</p>
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-left">
                  <th className="px-3 py-2 font-medium text-gray-500">{t('common.product')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.qty')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.unitPrice')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.discount')}</th>
                  <th className="px-3 py-2 font-medium text-gray-500 text-right">{t('common.subtotal')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {order.items.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2 text-gray-800">
                      {item.productTitle}
                      {formatCombo(item.variantCombo) && (
                        <span className="text-xs text-gray-400 ml-1.5">({formatCombo(item.variantCombo)})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-600">{item.quantity}</td>
                    <td className="px-3 py-2 text-right text-gray-600">৳{fmt(item.unitPrice)}</td>
                    <td className="px-3 py-2 text-right text-green-600">
                      {item.discountAmount > 0 ? `-৳${fmt(item.discountAmount)}` : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-800">৳{fmt(item.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                {order.manualDiscount > 0 && (
                  <tr className="border-t border-gray-100">
                    <td colSpan={4} className="px-3 py-2 text-sm text-gray-500 text-right">{t('orders.manualDiscountLabel')}</td>
                    <td className="px-3 py-2 text-right text-red-500 font-medium">-৳{fmt(order.manualDiscount)}</td>
                  </tr>
                )}
                {order.deliveryCharge > 0 && (
                  <tr className="border-t border-gray-100">
                    <td colSpan={4} className="px-3 py-2 text-sm text-gray-500 text-right">{t('orders.deliveryCharge')}</td>
                    <td className="px-3 py-2 text-right text-gray-700 font-medium">৳{fmt(order.deliveryCharge)}</td>
                  </tr>
                )}
                <tr className="border-t border-gray-200 bg-gray-50">
                  <td colSpan={4} className="px-3 py-2 text-sm font-semibold text-gray-700 text-right">{t('common.total')}</td>
                  <td className="px-3 py-2 text-right font-bold text-gray-900">৳{fmt(order.totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {order.notes && (
          <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <span className="font-medium text-gray-600">Note:</span> {order.notes}
          </p>
        )}

        {/* Payment & method */}
        <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
          <StatusBadge status={order.status} />
          <PaymentBadge
            status={order.paymentStatus}
            onClick={() => onPaymentChange(order.id, order.paymentStatus === 'PAID' ? 'UNPAID' : 'PAID')}
          />
          <MethodBadge method={order.paymentMethod} />
        </div>
      </div>
    </Modal>
  );
}

// ── Order item row (owns its own variant-picker query, since each row can pick a different product) ──

function OrderItemRow({ item, idx, products, onProductChange, onFieldChange, onRemove, canRemove }) {
  const { t } = useTranslation();

  const { data: variants } = useQuery({
    queryKey: ['product-variants-picker', item.productId],
    queryFn: () => getProductVariants(item.productId).then(r => r.data.data),
    enabled: !!item.productId,
  });

  const dimensionKeys = useMemo(() => {
    if (!variants || variants.length === 0) return [];
    const keys = new Set();
    variants.forEach(v => Object.keys(v.combo || {}).forEach(k => keys.add(k)));
    return Array.from(keys);
  }, [variants]);

  const optionsByDim = useMemo(() => {
    const map = {};
    dimensionKeys.forEach(k => { map[k] = []; });
    (variants || []).forEach(v => {
      dimensionKeys.forEach(k => {
        const val = v.combo?.[k];
        if (val != null && !map[k].includes(val)) map[k].push(val);
      });
    });
    return map;
  }, [variants, dimensionKeys]);

  const hasVariants = dimensionKeys.length > 0;
  const selectedCombo = item.variantCombo || {};
  const matchingVariant = hasVariants
    ? (variants || []).find(v => dimensionKeys.every(k => v.combo?.[k] === selectedCombo[k]))
    : null;

  // The backend only accepts variantId — this row lets the user pick per-dimension values for
  // usability, then resolves that combo to the matching variant's id and keeps it in sync.
  useEffect(() => {
    onFieldChange(idx, 'variantId', matchingVariant ? matchingVariant.id : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingVariant?.id]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <select
          value={item.productId}
          onChange={(e) => onProductChange(idx, e.target.value)}
          required
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
        >
          <option value="">{t('orders.selectProduct')}</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title} — ৳{fmt(p.price)}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={item.quantity}
          onChange={(e) => onFieldChange(idx, 'quantity', parseInt(e.target.value) || 1)}
          min="1"
          required
          className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
        />
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="p-2 text-gray-400 hover:text-red-500 transition-colors"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {hasVariants && (
        <div className="flex items-center gap-2 pl-1">
          {dimensionKeys.map(dim => (
            <select
              key={dim}
              value={selectedCombo[dim] || ''}
              onChange={(e) => onFieldChange(idx, 'variantCombo', { ...selectedCombo, [dim]: e.target.value })}
              required
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">{dim}</option>
              {optionsByDim[dim].map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          {matchingVariant && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              matchingVariant.stockQuantity === 0 ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-600'
            }`}>
              Available: {matchingVariant.stockQuantity}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Order Form ────────────────────────────────────────────────────────────────

function OrderForm({ mode, initialForm, products, onSubmit, onClose, isPending, error }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { productId: '', quantity: 1, variantCombo: null, variantId: null }] }));

  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const setItem = (idx, key, value) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) => (i === idx ? { ...item, [key]: value } : item)),
    }));

  // Changing the product must also clear any variant selection made for the previous product.
  const setItemProduct = (idx, productId) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((item, i) => (i === idx ? { ...item, productId, variantCombo: null, variantId: null } : item)),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <Modal title={mode === 'create' ? t('orders.new') : t('common.edit') + ' ' + t('orders.title')} onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Customer */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('common.customer')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.phone')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.customerPhone}
                onChange={(e) => setField('customerPhone', e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('orders.phonePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.customerName}
                onChange={(e) => setField('customerName', e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('orders.customerName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.email')}</label>
              <input
                type="email"
                value={form.customerEmail}
                onChange={(e) => setField('customerEmail', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.optional')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.customerAddress')}</label>
              <input
                type="text"
                value={form.customerAddress}
                onChange={(e) => setField('customerAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.optional')}
              />
            </div>
          </div>
        </div>

        {/* Items */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t('common.items')}</p>
          <div className="space-y-2">
            {form.items.map((item, idx) => (
              <OrderItemRow
                key={idx}
                item={item}
                idx={idx}
                products={products}
                onProductChange={setItemProduct}
                onFieldChange={setItem}
                onRemove={removeItem}
                canRemove={form.items.length > 1}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={addItem}
            className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <Plus size={14} /> {t('orders.addItem')}
          </button>
        </div>

        {/* Delivery & payment */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.deliveryAddress')}</label>
            <input
              type="text"
              value={form.deliveryAddress}
              onChange={(e) => setField('deliveryAddress', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('orders.deliveryAddressHint')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.paymentMethod')} <span className="text-red-500">*</span></label>
            <select
              value={form.paymentMethod}
              onChange={(e) => setField('paymentMethod', e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {PAYMENT_METHOD_KEYS.map((key) => (
                <option key={key} value={key}>{t(`paymentMethods.${key}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.paymentStatus')}</label>
            <select
              value={form.paymentStatus}
              onChange={(e) => setField('paymentStatus', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="UNPAID">{t('status.UNPAID')}</option>
              <option value="PAID">{t('status.PAID')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.manualDiscount')}</label>
            <input
              type="number"
              value={form.manualDiscount}
              onChange={(e) => setField('manualDiscount', e.target.value)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.deliveryCharge')}</label>
            <input
              type="number"
              value={form.deliveryCharge}
              onChange={(e) => setField('deliveryCharge', e.target.value)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
            <input
              type="text"
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('common.optional')}
            />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
          <button type="submit" disabled={isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
            {isPending ? t('common.saving') : t('orders.saveOrder')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Orders() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [viewOrder, setViewOrder] = useState(null);
  const [formModal, setFormModal] = useState(null);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banTarget, setBanTarget] = useState(null);
  const [banForm, setBanForm] = useState({ hours: '', reason: '' });
  const [banError, setBanError] = useState('');
  const [statusError, setStatusError] = useState('');

  usePageTitle('Orders');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWriteOrders(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const { data, isLoading } = useQuery({
    queryKey: ['orders', page, filters],
    queryFn: () => getOrders(page, 10, filters).then((r) => r.data.data),
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => getProducts(0, 100).then((r) => r.data.data.content),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  const buildPayload = (form) => ({
    customer: {
      name: form.customerName,
      phone: form.customerPhone,
      email: form.customerEmail || null,
      address: form.customerAddress || null,
    },
    deliveryAddress: form.deliveryAddress || null,
    notes: form.notes || null,
    paymentStatus: form.paymentStatus,
    paymentMethod: form.paymentMethod,
    manualDiscount: parseFloat(form.manualDiscount) || 0,
    deliveryCharge: parseFloat(form.deliveryCharge) || 0,
    items: form.items.map((i) => ({
      productId: parseInt(i.productId),
      quantity: parseInt(i.quantity),
      variantId: i.variantId || null,
    })),
  });

  const saveMutation = useMutation({
    mutationFn: (form) =>
      formModal?.mode === 'edit'
        ? updateOrder(formModal.data.id, buildPayload(form))
        : createOrder(buildPayload(form)),
    onSuccess: () => { invalidate(); setFormModal(null); setFormError(''); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteOrder(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateOrderStatus(id, status),
    onSuccess: (_, { id }) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (viewOrder?.id === id) {
        setViewOrder(null);
      }
    },
    onError: (err) => setStatusError(err.response?.data?.message || 'Something went wrong.'),
  });

  const paymentMutation = useMutation({
    mutationFn: ({ id, paymentStatus }) => updateOrderPayment(id, paymentStatus),
    onSuccess: (_, { id }) => {
      invalidate();
      if (viewOrder?.id === id) setViewOrder(null);
    },
  });

  const banMutation = useMutation({
    mutationFn: ({ id, data }) => banOrderFingerprint(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setBanTarget(null);
      setBanForm({ hours: '', reason: '' });
      setBanError('');
    },
    onError: (err) => setBanError(err.response?.data?.message || 'Something went wrong.'),
  });

  const openCreate = () => {
    setFormError('');
    setFormModal({ mode: 'create' });
  };

  const openEdit = (order) => {
    setFormError('');
    setFormModal({
      mode: 'edit',
      data: order,
      initialForm: {
        customerName: order.customer.name,
        customerPhone: order.customer.phone,
        customerEmail: order.customer.email || '',
        customerAddress: order.customer.defaultAddress || '',
        deliveryAddress: order.deliveryAddress || '',
        notes: order.notes || '',
        paymentStatus: order.paymentStatus,
        paymentMethod: order.paymentMethod || 'CASH',
        manualDiscount: order.manualDiscount || 0,
        deliveryCharge: order.deliveryCharge || 0,
        items: order.items.map((i) => ({
          productId: String(i.productId),
          quantity: i.quantity,
          variantCombo: i.variantCombo || null,
        })),
      },
    });
  };

  const canEdit = (order) => order.status === 'PENDING' || order.status === 'CONFIRMED';

  const orders = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('orders.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('common.total')}</p>
        </div>
        {write && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={15} /> {t('orders.new')}
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('orders.noOrders')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">#</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.customer')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.items')}</th>
                <th className="px-4 py-3 font-medium text-gray-500 text-right">{t('common.total')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.status')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.payment')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('orders.paymentMethod')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.date')}</th>
                <th className="px-4 py-3 w-24 font-medium text-gray-500">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((order) => (
                <tr key={order.id} className={`transition-colors ${order.fingerprintBanned ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-gray-50'}`}>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">#{order.token}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{order.customer.name}</p>
                    <p className="text-xs text-gray-400">{order.customer.phone}</p>
                    {order.takenBy && <p className="text-xs text-gray-300 mt-0.5">by {order.takenBy}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {order.items.length} item{order.items.length !== 1 ? 's' : ''}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-800">
                    ৳{fmt(order.totalAmount)}
                  </td>
                  <td className="px-4 py-3">
                    <InlineStatusChanger
                      order={order}
                      onStatusChange={(id, status) => statusMutation.mutate({ id, status })}
                      isPending={statusMutation.isPending}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <PaymentBadge
                      status={order.paymentStatus}
                      onClick={() => paymentMutation.mutate({
                        id: order.id,
                        paymentStatus: order.paymentStatus === 'PAID' ? 'UNPAID' : 'PAID',
                      })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <MethodBadge method={order.paymentMethod} />
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    <p>{new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    <p>{new Date(order.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <button onClick={() => setViewOrder(order)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors" title={t('orders.viewDetails')}>
                        <Eye size={15} />
                      </button>
                      {write && order.fingerprintKey && (
                        <button
                          onClick={() => setBanTarget(order)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            order.fingerprintBanned ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                          }`}
                          title={order.fingerprintBanned ? t('orders.deviceBanned') : t('orders.banDevice')}
                        >
                          <Ban size={15} />
                        </button>
                      )}
                      {write && canEdit(order) && (
                        <button onClick={() => openEdit(order)} className="p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                          <Pencil size={15} />
                        </button>
                      )}
                      {write && (
                        <button onClick={() => setDeleteTarget(order)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Detail Modal */}
      {viewOrder && (
        <OrderDetail
          order={viewOrder}
          onClose={() => setViewOrder(null)}
          onPaymentChange={(id, paymentStatus) => paymentMutation.mutate({ id, paymentStatus })}
          onBanRequest={write ? () => { setViewOrder(null); setBanTarget(viewOrder); } : undefined}
        />
      )}

      {/* Create / Edit Form */}
      {formModal && (
        <OrderForm
          mode={formModal.mode}
          initialForm={formModal.initialForm ?? EMPTY_FORM}
          products={products}
          onSubmit={(form) => saveMutation.mutate(form)}
          onClose={() => { setFormModal(null); setFormError(''); }}
          isPending={saveMutation.isPending}
          error={formError}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <Modal title={t('orders.deleteOrder')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600">
            <span className="font-medium text-gray-900">#{deleteTarget.token}</span>{' '}
            <span className="font-medium text-gray-900">{deleteTarget.customer.name}</span>?{' '}
            {t('common.cannotBeUndone')}
          </p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}

      {/* Status change failed (e.g. not enough stock to send) */}
      {statusError && (
        <Modal title={t('orders.statusChangeFailed')} onClose={() => setStatusError('')}>
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{statusError}</p>
          <div className="flex justify-end mt-5">
            <button onClick={() => setStatusError('')} className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors">OK</button>
          </div>
        </Modal>
      )}

      {/* Ban Device Confirmation */}
      {banTarget && (
        <Modal title={t('orders.banDevice')} onClose={() => { setBanTarget(null); setBanError(''); }}>
          <p className="text-sm text-gray-600 mb-4">
            {t('orders.banDeviceHint', { name: banTarget.customer.name })}
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('orders.banHours')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max="720"
                required
                value={banForm.hours}
                onChange={(e) => setBanForm((f) => ({ ...f, hours: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('orders.banReason')}</label>
              <input
                type="text"
                value={banForm.reason}
                onChange={(e) => setBanForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder={t('common.optional')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {banError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3">{banError}</p>
          )}
          <div className="flex items-center justify-between mt-5">
            <button
              onClick={() => banMutation.mutate({ id: banTarget.id, data: { hours: null } })}
              disabled={banMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              {banMutation.isPending && banMutation.variables?.data?.hours == null ? t('orders.unbanning') : t('orders.unban')}
            </button>
            <div className="flex gap-2">
              <button onClick={() => { setBanTarget(null); setBanError(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button
                onClick={() => banMutation.mutate({ id: banTarget.id, data: { hours: parseInt(banForm.hours), reason: banForm.reason || undefined } })}
                disabled={banMutation.isPending || !banForm.hours}
                className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white rounded-lg transition-colors"
              >
                {banMutation.isPending && banMutation.variables?.data?.hours != null ? t('orders.banning') : t('orders.confirmBan')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
